// src/utils/actionRecommender.js
// ============================================================
// NEXT BEST ACTION ENGINE
// Given a tenant's risk score and behavior, recommend the
// optimal action: which channel, what tone, what offer.
// ============================================================

/**
 * Generate recommended action for a tenant based on risk profile
 * @param {Object} tenant - Tenant record with risk score
 * @returns {Object} - { action, channel, tone, message, urgency }
 */
export function getRecommendedAction(tenant) {
  const tier = tenant.risk?.tier || 'minimal';
  const daysLate = tenant.currentDaysLate || 0;
  const outreachCount = (tenant.outreachLog || []).length;

  // --- HIGH RISK (85-100): aggressive but helpful ---
  if (tier === 'high') {
    // If already contacted multiple times with no response → escalate to call
    if (outreachCount >= 3) {
      return {
        action: 'Escalate to call + offer payment plan',
        channel: 'call',
        tone: 'direct',
        urgency: 'immediate',
        description: `${outreachCount} prior outreach attempts with no response. Escalate to direct call and offer structured payment plan.`,
        buttons: ['Call Tenant', 'Offer Payment Plan'],
      };
    }
    // First or second outreach → firm SMS + payment plan
    return {
      action: 'Send firm SMS + offer payment plan',
      channel: 'sms',
      tone: 'firm',
      urgency: 'today',
      description: `Balance of $${tenant.currentBalance?.toLocaleString()} is ${daysLate} days overdue. Send firm reminder with payment plan option.`,
      buttons: ['Send SMS', 'Offer Payment Plan', 'Call Tenant'],
    };
  }

  // --- MEDIUM RISK (60-84): proactive nudge ---
  if (tier === 'medium') {
    // If drift is worsening → offer payment flexibility
    if (tenant.risk?.factors?.drift > 50) {
      return {
        action: 'Send SMS with payment flexibility offer',
        channel: 'sms',
        tone: 'helpful',
        urgency: 'today',
        description: `Payment pattern is worsening (drift detected). Proactively offer flexible payment timing before situation escalates.`,
        buttons: ['Send SMS', 'Offer Split Payment'],
      };
    }
    // Standard medium risk → firm but friendly reminder
    return {
      action: 'Send reminder SMS',
      channel: 'sms',
      tone: 'firm',
      urgency: 'today',
      description: `Balance of $${tenant.currentBalance?.toLocaleString()} outstanding. Send reminder with clear balance and due date.`,
      buttons: ['Send SMS', 'Offer Payment Plan'],
    };
  }

  // --- LOW RISK (30-59): gentle nudge ---
  if (tier === 'low') {
    return {
      action: 'Send friendly reminder',
      channel: 'email',
      tone: 'friendly',
      urgency: 'this_week',
      description: `Minor balance or occasional lateness. A friendly reminder is sufficient.`,
      buttons: ['Send Email', 'Send SMS'],
    };
  }

  // --- MINIMAL RISK (0-29): no action ---
  return {
    action: 'No action needed',
    channel: 'none',
    tone: 'none',
    urgency: 'none',
    description: 'Tenant is in good standing. No intervention required.',
    buttons: [],
  };
}


/**
 * Generate insight line for the Action Feed card
 * One human-readable sentence explaining WHY this tenant is flagged
 * @param {Object} tenant - Tenant with risk data
 * @returns {string} - Insight sentence
 */
export function generateInsight(tenant) {
  const score = tenant.risk?.score || 0;
  const factors = tenant.risk?.factors || {};
  const daysLate = tenant.currentDaysLate || 0;

  // Find the dominant factor driving the score
  const dominant = Object.entries(factors)
    .sort(([,a], [,b]) => b - a)[0];

  if (!dominant) return 'Insufficient data for analysis.';

  const [factorName, factorValue] = dominant;

  switch (factorName) {
    case 'daysLate':
      return `Currently ${daysLate} days past due — this is the primary risk driver.`;
    case 'lateFrequency':
      return `Late on ${Math.round(factorValue)}% of payments in recent months — pattern of chronic lateness.`;
    case 'balanceRatio':
      return `Outstanding balance is ${(tenant.currentBalance / tenant.monthlyRent).toFixed(1)}x monthly rent — significant accumulation.`;
    case 'drift':
      if (factorValue > 60) return 'Payment timing is getting worse each month — drift detected.';
      return 'Payment pattern has shifted later over recent months.';
    case 'partialPayments':
      return 'Frequent partial payments suggest cash flow strain.';
    default:
      return score > 60 
        ? 'Multiple risk factors contributing to elevated score.'
        : 'Minimal risk indicators detected.';
  }
}
